<template>
  <div>
    <slot></slot>
  </div>
</template>

<script lang="js">
import debounce from '../../js/util/debounce';
import scrollTo from '../../js/util/scrollTo';
import supportsPassiveEventListeners from '../../js/util/supportsPassiveEventListeners';

export default  {
  name: 'ahc-exhibition',
  provide() {
    return {
      exhibition: this
    }
  },
  methods: {
    onScroll() {
      clearTimeout(this.stoppedScrollTimeout);
      cancelAnimationFrame(this.scrolSnapAnimation);
      this.pageYOffset = window.pageYOffset;

      // don't update the states of animations on the scroll event directly
      // use a rAF and prevent new calls while the current one is running
      this.requestTick();

      this.stoppedScrollTimeout = setTimeout(this.onStoppedScrolling, 400);
    },
    requestTick() {
      if (!this.ticking) {
        this.ticking = true;
        window.requestAnimationFrame(this.update);
      }
    },
    recurringTick() {
      this.requestTick();
      this.recurringTickTimeout = setTimeout(this.recurringTick, 2000);
    },
    update() {
      if (!this.ready) {
        return;
      }

      this.registeredSections.forEach(section => section.update());
      this.ticking = false;
    },
    refresh() {
      if (!this.ready) {
        return;
      }

      this.windowHeight = window.innerHeight;
      this.registeredSections.forEach(section => section.redraw());
      this.pageYOffset = window.pageYOffset;
      this.registeredSections.forEach(section => section.recalc());
      this.registeredSections.forEach(section => section.update());

      this.$emit('refresh');
    },
    init() {
      if (this.ready) {
        return;
      }

      this.ready = true;

      // "refresh" is a hook for doing more expensive work calculating the dimensions
      // of elements that may impact the on scroll animations and is called once
      // on mount, and then again on events which may change the dimensions, e.g. resize
      this.onResize = debounce(this.refresh, 200);
      window.addEventListener('resize', this.onResize, false);
      window.addEventListener('load', this.refresh, false);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.refresh).catch(err => console.error(err));
      }

      // "update" is a hook called on changes in scroll position which updates the state / progress
      // of scroll animations / effects
      window.addEventListener('scroll', this.onScroll, supportsPassiveEventListeners ? { passive: true } : false);

      // elements further up the page should have a higher zIndex so they can
      // reveal the next section when they slide up
      //
      // TODO: this may need to be more sophisticated if other transitions are added
      const childrenArray = Array.from(this.$el.children);

      childrenArray.forEach((el, index) => {
        el.style.zIndex = this.$el.children.length - index;
      });

      this.registeredSections.forEach(section => section.init());
      this.refresh();
      this.recurringTick();
    },
    destroy() {
      if (!this.ready) {
        return;
      }

      this.ready = false;

      window.removeEventListener('scroll', this.onScroll, false);
      window.removeEventListener('resize', this.onResize, false);
      window.removeEventListener('load', this.refresh, false);

      const childrenArray = Array.from(this.$el.children);

      childrenArray.forEach((el) => {
        el.style.zIndex = '';
      });

      clearTimeout(this.recurringTickTimeout);
      clearTimeout(this.stoppedScrollTimeout);
      cancelAnimationFrame(this.scrolSnapAnimation);

      this.registeredSections.forEach(section => section.destroy());

      this.ticking = false;
      this.windowHeight = 0;
    },
    onMatchMedia(breakpoint) {
      if (breakpoint.matches) {
        this.init();
      } else {
        this.destroy();
      }
    },
    onStoppedScrolling() {
      const scrollPosition = window.pageYOffset;
      const THRESHOLD = 300;

      // snap the cover to the top
      if (scrollPosition <= THRESHOLD) {
        this.scrolSnapAnimation = scrollTo(0, { duration: Math.abs(scrollPosition) * 2.5 });
        return;
      }

      for (let i = 0; i < this.registeredSections.length; i++) {
        const section = this.registeredSections[i];
        const difference = scrollPosition - section.scrollOffsetTop;

        // snap to the current section if near the top
        if (Math.abs(difference) <= THRESHOLD) {
          this.scrolSnapAnimation = scrollTo(section.scrollOffsetTop, { duration: Math.abs(difference) * 2.5 });
          break;
        }
      }
    },
    registerSection(section) {
      this.registeredSections.push(section);
    }
  },
  created() {
    this.ticking = false;
    this.ready = false;
    this.windowHeight = 0;
    this.registeredSections = [];
  },
  mounted() {
    // do not apply the effects on devices that don't support hover with their
    // primary input device, (assume touch), or if the user prefers reduced motion
    if (
      !window.matchMedia('(hover: hover)').matches ||
      window.matchMedia('(prefers-reduced-motion)').matches
    ) {
      return;
    }

    const breakpoint = window.matchMedia('(min-width: 1024px)');
    breakpoint.addListener(this.onMatchMedia)

    this.onMatchMedia(breakpoint);
  },
  beforeDestroy() {
    this.destroy();
  }
}
</script>
